import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import styles from './SpecialModeView.module.scss'
import { speak } from '../speech/speak'
import voices from '../speech/voices'
import { celebrate, popScale, shake } from './juice'

import CloseIcon from '@mui/icons-material/Close'
import VolumeUpIcon from '@mui/icons-material/VolumeUp'
import SkipNextIcon from '@mui/icons-material/SkipNext'
import TableChartIcon from '@mui/icons-material/TableChart'
import { macStudioFetch } from '../ollama'

// Personal endings are a fixed set that depends on the tense TYPE, not the verb.
// Cover symbols: I = i/ı/u/ü, A = a/e, (y) = buffer y after a vowel.
export const TR_TENSES = [
  { key: 'present', label: 'Present (-iyor)', type: 1, prompt: 'present continuous tense (-iyor)' },
  { key: 'aorist', label: 'Aorist (-r)', type: 1, prompt: 'aorist / habitual present (-r / -ir)' },
  { key: 'future', label: 'Future (-ecek)', type: 1, prompt: 'future tense (-ecek / -acak)' },
  { key: 'past', label: 'Past (-di)', type: 2, prompt: 'definite past tense (-di)' },
]
const PERSONS = [
  { key: 'ben', en: 'I' },
  { key: 'sen', en: 'you (informal)' },
  { key: 'o', en: 'he / she / it' },
  { key: 'biz', en: 'we' },
  { key: 'siz', en: 'you (plural / formal)' },
  { key: 'onlar', en: 'they' },
]
const ENDINGS = {
  1: { ben: '-(y)Im', sen: '-sIn', o: '— (none)', biz: '-(y)Iz', siz: '-sInIz', onlar: '-lAr(ler/lar)' },
  2: { ben: '-m', sen: '-n', o: '— (none)', biz: '-k', siz: '-nIz', onlar: '-lAr(ler/lar)' },
}
const PERSON_BY_KEY = Object.fromEntries(PERSONS.map(p => [p.key, p]))
const tenseOf = (k) => TR_TENSES.find(t => t.key === k) || TR_TENSES[0]

const norm = (s) => String(s || '').normalize('NFC').toLocaleLowerCase('tr-TR').replace(/[^\p{L}\p{N}]/gu, '').trim()
const shuffle = (a) => { const b = [...a]; for (let i = b.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1));[b[i], b[j]] = [b[j], b[i]] } return b }

const parseItems = (raw) => {
  if (!raw) return []
  let t = String(raw).replace(/```json/gi, '').replace(/```/g, '').trim()
  const a = t.indexOf('{'), b = t.lastIndexOf('}')
  if (a !== -1 && b !== -1) t = t.slice(a, b + 1)
  try { const o = JSON.parse(t); return Array.isArray(o?.items) ? o.items : [] } catch { return [] }
}
const shapeItem = (raw) => {
  const conjugated = String(raw?.conjugated || '').trim()
  const person = String(raw?.person || '').trim().toLowerCase()
  if (!conjugated || !PERSON_BY_KEY[person]) return null
  let stem = String(raw?.stem || '').trim()
  let ending = String(raw?.ending || '').trim()
  if (person === 'o') { stem = conjugated; ending = '' }
  else if (!ending || norm(stem + ending) !== norm(conjugated)) {
    if (stem && conjugated.toLowerCase().startsWith(stem.toLowerCase())) ending = conjugated.slice(stem.length)
    else { stem = ''; ending = conjugated }
  }
  return { verb: String(raw?.verb || '').trim(), meaning: String(raw?.meaning || '').trim(), person, conjugated, stem, ending }
}

export default function TurkishVerbView({ tenseKey, count = 10, query = '', voiceSpeed, onBack, onClose }) {
  const tense = tenseOf(tenseKey)
  const type = tense.type

  const [phase, setPhase] = useState('loading') // loading | drill | error
  const [errorMsg, setErrorMsg] = useState('')
  const [items, setItems] = useState([])
  const [idx, setIdx] = useState(0)
  const [setNum, setSetNum] = useState(1)
  const [regen, setRegen] = useState(false)

  const [stage, setStage] = useState('pick') // pick | produce | done
  const [choice, setChoice] = useState(null)
  const [pickResult, setPickResult] = useState(null) // right | wrong | revealed
  const [pickAttempts, setPickAttempts] = useState(0)
  const [options, setOptions] = useState([])
  const [typed, setTyped] = useState('')
  const [endAttempts, setEndAttempts] = useState(0)
  const [endRevealed, setEndRevealed] = useState(false)
  const [combo, setCombo] = useState(0)
  const [best, setBest] = useState(0)
  const [showRef, setShowRef] = useState(false)

  const item = items[idx]
  const advanceTimerRef = useRef(null)
  const inputRef = useRef(null)
  const cardRef = useRef(null)
  const comboRef = useRef(null)
  const pickRowRef = useRef(null)
  const completedRef = useRef(false)
  useEffect(() => { if (combo > 0) popScale(comboRef.current) }, [combo])

  const generate = useCallback(async (isRegen = false) => {
    if (isRegen) setRegen(true); else setPhase('loading')
    const prompt = `You are a Turkish teacher creating a verb-conjugation drill in the ${tense.prompt}.
${query.trim() ? `Focus: "${query.trim()}".` : 'Use common, beginner-friendly verbs.'}

Create ${count} items. Each: ONE verb conjugated for ONE subject (ben, sen, o, biz, siz, onlar) in the ${tense.prompt}. Vary the subject and the verbs across the set.
For each item:
- verb: the infinitive (e.g. "gelmek")
- meaning: short English meaning ("to come")
- person: one of "ben","sen","o","biz","siz","onlar"
- conjugated: the verb correctly conjugated for that subject in the ${tense.prompt}, WITHOUT the pronoun (apply vowel harmony and any consonant changes)
- stem: the part of "conjugated" BEFORE the personal ending (the verb + tense marker)
- ending: the personal ending actually attached (harmonised), so that stem + ending EXACTLY equals conjugated (empty for "o")

Return ONLY minified JSON, no markdown:
{"items":[{"verb":"","meaning":"","person":"","conjugated":"","stem":"","ending":""}]}`
    try {
      const res = await macStudioFetch('generate', JSON.stringify({ model: 'gemma4:26b', prompt, stream: false, keep_alive: '30m', format: 'json', options: { temperature: 0.3, num_predict: 2200 } }))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const data = await res.json()
      const built = parseItems(data.response).map(shapeItem).filter(Boolean)
      if (built.length === 0) { setErrorMsg('The model returned no usable items — try again.'); setPhase('error'); setRegen(false); return }
      setItems(built); setIdx(0); setSetNum(n => (isRegen ? n + 1 : 1)); setRegen(false); setPhase('drill')
    } catch (e) {
      setErrorMsg((e.message || '').match(/Failed to fetch|Load failed/) ? 'Connection to the Mac Studio failed — check the tunnel / Ollama.' : (e.message || 'Generation failed.'))
      setPhase('error'); setRegen(false)
    }
  }, [tense, query, count])

  useEffect(() => { generate(false) }, []) // eslint-disable-line

  // Reset per item + shuffle the 6 ending options.
  useEffect(() => {
    if (phase !== 'drill' || !item) return
    setStage('pick'); setChoice(null); setPickResult(null); setPickAttempts(0)
    setTyped(''); setEndAttempts(0); setEndRevealed(false)
    completedRef.current = false
    setOptions(shuffle(PERSONS.map(p => ({ person: p.key, ending: ENDINGS[type][p.key] }))))
  }, [idx, phase, item, type])

  useEffect(() => { if (stage === 'produce') setTimeout(() => inputRef.current?.focus(), 60) }, [stage])

  const advance = useCallback(() => {
    clearTimeout(advanceTimerRef.current)
    setIdx(prev => { const next = prev + 1; if (next >= items.length) { generate(true); return prev } return next })
  }, [items.length, generate])

  const completeItem = useCallback(() => {
    if (completedRef.current) return
    completedRef.current = true
    setStage('done')
    const el = cardRef.current
    if (el) { const r = el.getBoundingClientRect(); celebrate(r.left + r.width / 2, r.top + r.height / 2, { count: 28, spread: 170 }) }
    setCombo(c => { const nc = c + 1; setBest(b => Math.max(b, nc)); if (nc % 5 === 0) celebrate(window.innerWidth / 2, window.innerHeight / 2.4, { count: 64, spread: 260 }); return nc })
    advanceTimerRef.current = setTimeout(advance, 900)
  }, [advance])

  const pick = (personKey) => {
    if (!item || pickResult === 'right' || pickResult === 'revealed') return
    setChoice(personKey)
    if (personKey === item.person) { setPickResult('right'); setTimeout(() => setStage('produce'), 750) }
    else {
      const n = pickAttempts + 1; setPickAttempts(n); setPickResult('wrong'); shake(pickRowRef.current)
      if (n >= 2) { setChoice(item.person); setPickResult('revealed') }
    }
  }

  const isCorrect = (val) => norm(val) === norm(item.conjugated) || norm((item.stem || '') + val) === norm(item.conjugated)
  const onTyped = (val) => { setTyped(val); if (item && isCorrect(val)) completeItem() }
  const checkEnding = () => {
    if (!item || completedRef.current) return
    if (isCorrect(typed)) return completeItem()
    const n = endAttempts + 1; setEndAttempts(n); shake(inputRef.current)
    if (n >= 5) setEndRevealed(true)
  }
  const skip = () => { setCombo(0); advance() }
  const flagWrong = () => advance()
  const hear = () => { if (item) speak(item.conjugated, voices.tur || voices['eng'], voiceSpeed) }

  // ── loading / error ────────────────────────────────────────────────────────
  if (phase !== 'drill') {
    return (
      <div className={styles.view}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.panel}>
          <div className={styles.modeLabel}>Turkish · Verb Endings</div>
          {phase === 'loading'
            ? <div className={styles.loading}><div className={styles.spinner} /><div className={styles.loadingText}>Building {count} conjugations in the {tense.label}…</div></div>
            : <>
                <div className={styles.error}>{errorMsg}</div>
                <div style={{ display: 'flex', gap: '0.6rem', marginTop: '0.6rem' }}><button className={styles.linkBtn} onClick={onBack}>Back</button><button className={styles.generateBtn} onClick={() => generate(false)}>Try again</button></div>
              </>}
        </div>
      </div>
    )
  }

  const person = PERSON_BY_KEY[item?.person]
  const correctEnding = ENDINGS[type][item?.person]
  const revealLetters = endRevealed ? (item?.ending || '').length : Math.max(0, Math.min((item?.ending || '').length, endAttempts - 1))
  const scaffold = (item?.stem || '') + (item?.ending || '').slice(0, revealLetters)
  const scaffoldBlanks = '_'.repeat(Math.max(0, (item?.ending || '').length - revealLetters))

  return (
    <div className={styles.view}>
      <button className={styles.closeBtn} onClick={onBack}><CloseIcon /></button>

      <div className={styles.drill}>
        <div className={styles.drillTop}>
          <span className={styles.modeLabel}>Set {setNum} · {tense.label} · Turkish</span>
          <div className={styles.comboWrap}><span className={styles.comboFire}>{combo >= 3 ? '🔥' : '✦'}</span><span className={styles.combo} ref={comboRef}>{combo}</span></div>
        </div>

        <div className={styles.card} key={idx} ref={cardRef}>
          <div className={styles.stageLabel}>conjugate <strong>{item?.verb}</strong>{item?.meaning ? ` — ${item.meaning}` : ''} · <span style={{ color: '#6ee7b7' }}>{person?.key}</span> ({person?.en})</div>

          {/* stage 1: pick the personal ending */}
          {stage === 'pick' && (
            <>
              <div className={styles.stageLabel}>Which personal ending goes with <strong>{person?.key}</strong>?</div>
              <div className={styles.caseGrid} ref={pickRowRef}>
                {options.map(o => {
                  const chosen = choice === o.person
                  const isAnswer = o.person === item?.person
                  const cls = [styles.caseBtn, pickResult && isAnswer && (pickResult === 'right' || pickResult === 'revealed') ? styles.caseRight : '', chosen && pickResult === 'wrong' && !isAnswer ? styles.caseWrong : ''].join(' ')
                  return <button key={o.person} className={cls} onClick={() => pick(o.person)}><span className={styles.caseName} style={{ fontFamily: "'DM Mono', monospace" }}>{o.ending}</span></button>
                })}
              </div>
              <button className={styles.hintBtn} onClick={() => setShowRef(true)}>📋 endings table</button>
              {pickResult === 'wrong' && pickAttempts < 2 && <div className={styles.nudge}>Not that one — check the endings table, then try again.</div>}
              {pickResult === 'revealed' && <><div className={styles.whyBox}><strong>{person?.key}</strong> takes <strong>{correctEnding}</strong> in {tense.label}.</div><button className={styles.checkBtn} onClick={() => setStage('produce')}>Continue →</button></>}
            </>
          )}

          {/* stage 2: type the full conjugation */}
          {stage === 'produce' && (
            <>
              <div className={styles.whyBox}><strong>{person?.key}</strong> → <strong>{correctEnding}</strong> ✓ — now build the whole word (apply vowel harmony).</div>
              <div className={styles.stageLabel}>Write <strong>{item?.verb}</strong> for <strong>{person?.key}</strong> in the {tense.label}:</div>
              <div className={styles.endingScaffold}><span className={styles.stem}>{scaffold}</span><span className={styles.blankInline}>{scaffoldBlanks}</span></div>
              <input ref={inputRef} className={styles.answerInput} value={typed} onChange={e => onTyped(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') checkEnding() }} placeholder="type the conjugated verb…" spellCheck="false" autoComplete="off" />
              <div className={styles.endActions}>
                <button className={styles.checkBtn} onClick={checkEnding}>Check</button>
                {endAttempts >= 1 && <span className={styles.endHint}>💡 {item?.ending ? `${correctEnding} here → the ending is ${item.ending.length} letter${item.ending.length > 1 ? 's' : ''}; harmonise the vowel` : '3rd person singular — no personal ending is added'}</span>}
                {!endRevealed && <button className={styles.linkBtn} onClick={() => setEndRevealed(true)}>reveal</button>}
              </div>
              {endRevealed && <div className={styles.revealNote}>Answer: <strong>{item?.conjugated}</strong></div>}
            </>
          )}

          {stage === 'done' && <div className={styles.doneMsg}>{pickAttempts === 0 && endAttempts === 0 ? '★ Perfect!' : '✓ Nice'}</div>}
        </div>

        <div className={styles.controls}>
          <button className={styles.sideBtn} onClick={hear} title="Hear it"><VolumeUpIcon /></button>
          <button className={styles.sideBtn} onClick={() => setShowRef(true)} title="Personal-endings table"><TableChartIcon /></button>
          <button className={styles.sideBtn} onClick={skip} title="Skip (resets streak)"><SkipNextIcon /></button>
        </div>
        <div className={styles.footRow}>
          <span className={styles.progress}>{idx + 1} / {items.length} · best {best}</span>
          <button className={styles.linkBtn} onClick={flagWrong} title="Skip a dubious exercise — keeps your streak">⚑ looks wrong</button>
        </div>
      </div>

      {regen && <div className={styles.regenOverlay}><div className={styles.spinner} /><div className={styles.loadingText}>New set…</div></div>}
      {showRef && <EndingsRef type={type} person={item?.person} onClose={() => setShowRef(false)} />}
    </div>
  )
}

// Personal-endings reference: both types, current type + person spotlighted.
function EndingsRef({ type, person, onClose }) {
  return (
    <div className={styles.refOverlay} onClick={onClose}>
      <div className={styles.refModal} onClick={e => e.stopPropagation()} style={{ maxWidth: '520px' }}>
        <button className={styles.closeBtn} onClick={onClose}><CloseIcon /></button>
        <div className={styles.modeLabel}>Personal endings</div>
        <div className={styles.refStatus}>Type {type} — used by {type === 1 ? 'present, aorist, future' : 'the -di past'}. Your person: <strong className={styles.refHi}>{person}</strong></div>
        <div className={styles.refTable}>
          <div className={`${styles.refRow} ${styles.refHeadRow}`} style={{ gridTemplateColumns: '90px 1fr 1fr' }}><div>person</div><div>Type 1 (‑iyor/‑r/‑ecek)</div><div>Type 2 (‑di past)</div></div>
          {PERSONS.map(p => {
            const hi = p.key === person
            return (
              <div key={p.key} className={`${styles.refRow} ${hi ? styles.refTarget : ''}`} style={{ gridTemplateColumns: '90px 1fr 1fr' }}>
                <div className={styles.refGN}>{p.key}<span style={{ color: '#555560' }}> · {p.en.split(' ')[0]}</span></div>
                <div className={type === 1 ? styles.refNoun : ''} style={{ fontFamily: "'DM Mono', monospace", opacity: type === 1 ? 1 : 0.4 }}>{ENDINGS[1][p.key]}</div>
                <div className={type === 2 ? styles.refNoun : ''} style={{ fontFamily: "'DM Mono', monospace", opacity: type === 2 ? 1 : 0.4 }}>{ENDINGS[2][p.key]}</div>
              </div>
            )
          })}
        </div>
        <div className={styles.refStatus} style={{ fontSize: '0.82rem', color: '#94a3b8' }}>Vowel harmony: <b>I</b> = i/ı/u/ü, <b>A</b> = a/e. <b>(y)</b> is a buffer after a vowel.</div>
        <div className={styles.refFoot}><button className={styles.checkBtn} onClick={onClose}>Got it</button></div>
      </div>
    </div>
  )
}
